import { Close } from "@mui/icons-material";
import { Box, css, IconButton, styled, Typography } from "@mui/material";
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { useDeviceOrientation } from "contexts/panelMode";
import { PanelPickerOverlayContext } from "contexts/panelPickerOverlay";

import { WindowMobileTargetContext } from "../windowMobileTargetContext";
import { DRAWER_ANIMATION_DURATION, DRAWER_COLORS, DRAWER_LAYOUT, DRAWER_WIDTH, DRAWER_Z_INDEX } from "./constants";

type SnapPoint = 50 | 75 | 100;

// Fast-swipe threshold in px/ms — above this, snap to next point in swipe direction
const VELOCITY_THRESHOLD = 0.5;

// Landscape-only dismiss: drag down past 30% of drawer height to close
const LANDSCAPE_DISMISS_THRESHOLD = 0.3;

// Single container for both orientations — keeping it one component type means
// React never unmounts children on rotation, preserving drawer content state.
const DrawerContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPortrait",
})<{ isPortrait: boolean }>(
    ({ isPortrait }) => css`
        position: fixed;
        bottom: 0;
        background-color: ${DRAWER_COLORS.BG};
        border-radius: 16px 16px 0 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: ${DRAWER_Z_INDEX};
        pointer-events: auto;

        ${isPortrait
            ? css`
                  left: 0;
                  right: 0;
                  /* Portrait height is set inline from the current snap point — see the
                     height-sync useLayoutEffect in DrawerComponent. Deliberately no class
                     rule here to avoid a misleading fallback that could suggest 100dvh. */
                  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
                  overscroll-behavior: none;
              `
            : css`
                  right: ${DRAWER_LAYOUT.RIGHT_MARGIN}px;
                  top: ${DRAWER_LAYOUT.TOP_BAR_HEIGHT}px;
                  width: ${DRAWER_WIDTH}px;
                  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
              `}
    `,
);

const DragHandleArea = styled(Box)`
    min-height: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

const DragHandle = styled(Box)`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: ${DRAWER_COLORS.BORDER};
    flex-shrink: 0;
    position: relative;
    z-index: 11;
`;

const DrawerHeader = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: ${DRAWER_COLORS.HEADER_BG};
        min-height: 48px;
        flex-shrink: 0;
    `,
);

const HeaderLeft = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${DRAWER_COLORS.TEXT};
`;

const HeaderIcon = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 20px;
        height: 20px;
        color: ${DRAWER_COLORS.ICON};
    }
`;

const CloseButton = styled(IconButton)`
    padding: 8px;
    background-color: ${DRAWER_COLORS.BUTTON_BG};
    border-radius: 8px;
    color: ${DRAWER_COLORS.ICON};

    &:hover {
        background-color: ${TOOLBAR_COLORS.HOVER_DARK};
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const DrawerBody = styled(Box)(
    () => css`
        flex: 1;
        min-height: 0;
        overflow: auto;
        background-color: ${DRAWER_COLORS.BG};
        -webkit-overflow-scrolling: touch;
    `,
);

export interface DrawerComponentProps {
    /** Drawer title */
    title: string;
    /** Optional subtitle shown below the title (e.g. selected object name) */
    subtitle?: string;
    /** Optional icon */
    icon?: ReactNode;
    /** Drawer content */
    children: ReactNode;
    /** Whether drawer is open */
    isOpen: boolean;
    /** Close handler */
    onClose: () => void;
    /**
     * When true, the drag handle and internal header (title, close button) are not rendered.
     * Use when the drawer content supplies its own header.
     */
    hideHeader?: boolean;
    /** Optional test ID */
    "data-testid"?: string;
}

/**
 * Drawer Component
 *
 * A panel that slides in from the edge of the screen for phone UI.
 * - Portrait: slides up from bottom, full width. Supports snap points at 50%, 75%, 100%.
 *   Slow drag snaps to nearest threshold (dismisses below 30vh); fast swipe steps one snap
 *   point in the swipe direction.
 * - Landscape: slides in from right, 375px width, full height below top bar. Dismiss-only.
 */
export function DrawerComponent({
    title,
    subtitle,
    icon,
    children,
    isOpen,
    onClose,
    hideHeader = false,
    "data-testid": dataTestId,
}: DrawerComponentProps) {
    const orientation = useDeviceOrientation();
    const isPortrait = orientation === "portrait";

    const [pickerOverlay, setPickerOverlay] = useState<ReactNode>(null);
    const overlayContextValue = useMemo(() => ({ setOverlay: setPickerOverlay }), []);

    // Snap point is authoritative on the container's portrait height (see the height-sync
    // useLayoutEffect below). State drives the effect; the ref mirror lets the stable
    // touch-handler closure read the current value synchronously. Call sites update both.
    const [snapPoint, setSnapPoint] = useState<SnapPoint>(50);
    const snapPointRef = useRef<SnapPoint>(50);

    const containerRef = useRef<HTMLDivElement>(null);
    const dragZoneRef = useRef<HTMLDivElement>(null); // drag handle + header (portrait)
    const dragStartX = useRef<number | null>(null);
    const dragStartY = useRef<number | null>(null);
    // Drawer height in dvh captured at touchstart. Drag math uses this as the baseline
    // (rather than snapPointRef.current) so a drag that begins mid snap-transition
    // continues from the currently visible height instead of the last committed target.
    const dragStartHeightVh = useRef(50);
    const dragOffset = useRef(0);
    const gestureMode = useRef<"undecided" | "dragging" | "scrolling">("undecided");
    const scrollableAncestor = useRef<HTMLElement | null>(null);
    const lastTouchY = useRef(0);
    const lastTouchTime = useRef(0);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Portrait: reset to off-screen before first paint so the entry animation has a start position.
    // useLayoutEffect runs synchronously before the browser paints, guaranteeing the reset is
    // committed before the useEffect below reads getBoundingClientRect() to flush layout.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || !isPortrait) {
            return;
        }
        el.style.transition = "none";
        el.style.transform = "translateY(100%)";
    }, [isPortrait]);

    // Keep the container's inline height in sync with the current snap point.
    // - Portrait: `${snapPoint}dvh` bounds DrawerBody's scroll viewport (flex: 1) to the
    //   visible portion of the drawer at every snap. Any inline height set by touchmove
    //   is overwritten here on the next state commit.
    // - Landscape: clear the inline height. Landscape sizes via `top + bottom` in the
    //   styled block; a leftover inline height (e.g. from an interrupted portrait drag
    //   during rotation) would clobber that layout otherwise.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        if (!isPortrait) {
            el.style.height = "";
            return;
        }
        el.style.height = `${snapPoint}dvh`;
    }, [snapPoint, isPortrait]);

    // Animate in/out when isOpen changes (or when orientation flips).
    // Always uses explicit inline transforms so stale transforms from touch handlers
    // can never block a subsequent open — important now that DrawerComponent stays mounted.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }

        if (!isPortrait) {
            // Landscape: explicit inline animation keeps CSS and touch-handler state in sync
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
            el.style.transform = isOpen ? "translateY(0)" : "translateY(100%)";
            return;
        }

        if (isOpen) {
            // Reset to default snap on each open so snap state doesn't leak between sessions.
            // The height-sync useLayoutEffect picks this up and applies 50dvh inline. Every
            // path that mutates inline height (touchmove, snap-commit, drag-to-close) also
            // resyncs it against snapPointRef, so state and inline never disagree here.
            snapPointRef.current = 50;
            setSnapPoint(50);
            // Flush layout so the useLayoutEffect's translateY(100%) is committed before
            // the transition starts — prevents the browser from skipping the animation.
            void el.getBoundingClientRect();
            // Transform-only transition: the height-sync effect writes 50dvh inline without
            // a height transition, so the drawer doesn't visibly resize as it slides in.
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
            el.style.transform = "translateY(0)";
        } else {
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
            el.style.transform = "translateY(100%)";
        }
    }, [isOpen, isPortrait]);

    // Portrait: snap-point drag handling on the drag zone (handle + header)
    useEffect(() => {
        if (!isPortrait) {
            return;
        }
        const el = containerRef.current;
        const dragZone = dragZoneRef.current;
        if (!el || !dragZone) {
            return;
        }

        const snapDown = (sp: SnapPoint): SnapPoint | "closed" => {
            if (sp === 100) {
                return 75;
            }
            if (sp === 75) {
                return 50;
            }
            return "closed";
        };

        const snapUp = (sp: SnapPoint): SnapPoint => {
            if (sp === 50) {
                return 75;
            }
            return 100;
        };

        // Find the nearest snap point for a given effective vh value.
        // Midpoints between snap points: 50↔75 midpoint = 62.5, 75↔100 midpoint = 87.5.
        // Below 30vh → dismiss.
        const findNearestSnap = (effectiveVh: number): SnapPoint | "closed" => {
            if (effectiveVh < 30) {
                return "closed";
            }
            if (effectiveVh < 62.5) {
                return 50;
            }
            if (effectiveVh < 87.5) {
                return 75;
            }
            return 100;
        };

        const handleTouchStart = (e: TouchEvent) => {
            dragStartY.current = e.touches[0].clientY;
            dragOffset.current = 0;
            lastTouchY.current = e.touches[0].clientY;
            lastTouchTime.current = e.timeStamp;

            // Freeze any in-flight snap transition and capture the current visible height
            // as the drag baseline. Without this, a drag that starts mid-transition would
            // jump because touchmove would recompute from the last committed snap target
            // rather than what the user actually sees on screen.
            el.style.transition = "none";
            const computedHeightPx = parseFloat(getComputedStyle(el).height);
            const startHeightVh = (computedHeightPx / window.innerHeight) * 100;
            dragStartHeightVh.current = startHeightVh;
            el.style.height = `${startHeightVh}dvh`;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (dragStartY.current === null) {
                return;
            }
            e.preventDefault();
            const delta = e.touches[0].clientY - dragStartY.current;
            dragOffset.current = delta;

            // Resize the container to follow the finger. Drag DOWN (positive delta) shrinks
            // the drawer; drag UP (negative delta) grows it. Bottom-anchored positioning
            // (`bottom: 0`) means the top edge moves with the finger. Clamped between 0 and
            // 100 dvh; a 0-height release is picked up by findNearestSnap → "closed".
            const deltaVh = (-delta / window.innerHeight) * 100;
            const newHeightVh = Math.max(0, Math.min(100, dragStartHeightVh.current + deltaVh));
            el.style.height = `${newHeightVh}dvh`;

            lastTouchY.current = e.touches[0].clientY;
            lastTouchTime.current = e.timeStamp;
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (dragStartY.current === null) {
                return;
            }

            // Intentionally measured from the last move event, not gesture start —
            // this captures a quick flick at the end of an otherwise slow drag.
            const timeDelta = e.timeStamp - lastTouchTime.current;
            const yDelta = e.changedTouches[0].clientY - lastTouchY.current;
            // Positive velocity = moving down (collapse), negative = moving up (expand)
            const velocity = timeDelta > 0 ? yDelta / timeDelta : 0;

            let nextSnap: SnapPoint | "closed";

            if (velocity > VELOCITY_THRESHOLD) {
                nextSnap = snapDown(snapPointRef.current);
            } else if (velocity < -VELOCITY_THRESHOLD) {
                nextSnap = snapUp(snapPointRef.current);
            } else {
                // Base on the captured drag-start height (see touchstart) so a drag begun
                // mid-transition snaps relative to where the user actually started, not
                // the previous transition's target.
                const totalDeltaVh = (dragOffset.current / window.innerHeight) * 100;
                const effectiveVh = dragStartHeightVh.current - totalDeltaVh;
                nextSnap = findNearestSnap(effectiveVh);
            }

            if (nextSnap === "closed") {
                // Transform-only transition on close: the drawer slides off-screen and the
                // height snaps instantly back to the last committed snap so a subsequent
                // reopen finds inline height in sync with snapPointRef / state.
                el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;
                el.style.height = `${snapPointRef.current}dvh`;
                el.style.transform = "translateY(100%)";
                setTimeout(() => onCloseRef.current(), DRAWER_ANIMATION_DURATION);
            } else {
                // Animate both height (drag-end value → snap value) and transform (no-op
                // here, but included so any pending transform commits cleanly).
                el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out, height ${DRAWER_ANIMATION_DURATION}ms ease-out`;
                // Set inline height explicitly so the transition still runs when the snap
                // point didn't change (in which case setSnapPoint is a no-op and the
                // height-sync useLayoutEffect wouldn't fire to restore the value).
                el.style.height = `${nextSnap}dvh`;
                snapPointRef.current = nextSnap;
                setSnapPoint(nextSnap);
            }

            dragStartY.current = null;
            dragOffset.current = 0;
        };

        dragZone.addEventListener("touchstart", handleTouchStart, { passive: true });
        dragZone.addEventListener("touchmove", handleTouchMove, { passive: false });
        dragZone.addEventListener("touchend", handleTouchEnd, { passive: true });
        dragZone.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            dragZone.removeEventListener("touchstart", handleTouchStart);
            dragZone.removeEventListener("touchmove", handleTouchMove);
            dragZone.removeEventListener("touchend", handleTouchEnd);
            dragZone.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [isPortrait]);

    // Landscape: dismiss-only drag on the whole container (with scroll-aware gesture detection)
    useEffect(() => {
        if (isPortrait) {
            return;
        }
        const el = containerRef.current;
        if (!el) {
            return;
        }

        // Walk up from the touch target to find the nearest scrollable element
        // within the drawer, so we can tell content scrolling apart from a
        // drag-to-dismiss gesture.
        const findScrollableAncestor = (target: EventTarget | null): HTMLElement | null => {
            let node = target instanceof HTMLElement ? target : null;
            while (node && node !== el) {
                const style = getComputedStyle(node);
                const overflowY = style.overflowY;
                const overflowX = style.overflowX;
                const canScrollY =
                    (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
                const canScrollX =
                    (overflowX === "auto" || overflowX === "scroll") && node.scrollWidth > node.clientWidth;
                if (canScrollY || canScrollX) {
                    return node;
                }
                node = node.parentElement;
            }
            return null;
        };

        const handleTouchStart = (e: TouchEvent) => {
            dragStartX.current = e.touches[0].clientX;
            dragStartY.current = e.touches[0].clientY;
            dragOffset.current = 0;
            gestureMode.current = "undecided";
            scrollableAncestor.current = findScrollableAncestor(e.target);
            el.style.transition = "none";
        };

        const AXIS_THRESHOLD = 6;

        const handleTouchMove = (e: TouchEvent) => {
            if (dragStartX.current === null || dragStartY.current === null) {
                return;
            }
            const deltaX = e.touches[0].clientX - dragStartX.current;
            const deltaY = e.touches[0].clientY - dragStartY.current;

            if (gestureMode.current === "undecided") {
                if (Math.abs(deltaX) < AXIS_THRESHOLD && Math.abs(deltaY) < AXIS_THRESHOLD) {
                    return;
                }
                // Horizontal gesture, downward-from-not-top, or any upward scroll
                // belongs to the content; only a downward pull from the top dismisses.
                const ancestor = scrollableAncestor.current;
                const atTop = !ancestor || ancestor.scrollTop <= 0;
                if (Math.abs(deltaX) > Math.abs(deltaY) || deltaY <= 0 || !atTop) {
                    gestureMode.current = "scrolling";
                } else {
                    gestureMode.current = "dragging";
                }
            }

            if (gestureMode.current !== "dragging") {
                return;
            }

            if (e.cancelable) {
                e.preventDefault();
            }
            dragOffset.current = Math.max(0, deltaY);
            el.style.transform = `translateY(${dragOffset.current}px)`;
        };

        const handleTouchEnd = () => {
            if (dragStartY.current === null) {
                return;
            }

            const wasDragging = gestureMode.current === "dragging";
            dragStartX.current = null;
            dragStartY.current = null;
            scrollableAncestor.current = null;
            gestureMode.current = "undecided";

            if (!wasDragging) {
                return;
            }

            const size = el.offsetHeight;
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;

            if (dragOffset.current > size * LANDSCAPE_DISMISS_THRESHOLD) {
                el.style.transform = "translateY(100%)";
                setTimeout(() => onCloseRef.current(), DRAWER_ANIMATION_DURATION);
            } else {
                el.style.transform = "translateY(0)";
            }

            dragOffset.current = 0;
        };

        el.addEventListener("touchstart", handleTouchStart, { passive: true });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        el.addEventListener("touchend", handleTouchEnd, { passive: true });
        el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [isPortrait]);

    return (
        <PanelPickerOverlayContext.Provider value={overlayContextValue}>
            <WindowMobileTargetContext.Provider value={{ target: "drawer", isOpen }}>
                <DrawerContainer ref={containerRef} isPortrait={isPortrait} data-testid={dataTestId}>
                    <Box ref={dragZoneRef}>
                        <DragHandleArea>
                            <DragHandle />
                        </DragHandleArea>
                        {!hideHeader && (
                            <DrawerHeader className="drawer-header">
                                <HeaderLeft>
                                    {icon && <HeaderIcon>{icon}</HeaderIcon>}
                                    <Box display="flex" flexDirection="column">
                                        <Typography
                                            variant={subtitle ? "caption" : "subtitle1"}
                                            fontWeight={600}
                                            color={subtitle ? "text.secondary" : undefined}
                                        >
                                            {title}
                                        </Typography>
                                        {subtitle && (
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                </HeaderLeft>
                                <CloseButton onClick={onClose} size="small" title="Close drawer">
                                    <Close />
                                </CloseButton>
                            </DrawerHeader>
                        )}
                    </Box>
                    <DrawerBody>{children}</DrawerBody>
                    {pickerOverlay !== null && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 10,
                                bgcolor: "white",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {pickerOverlay}
                        </Box>
                    )}
                </DrawerContainer>
            </WindowMobileTargetContext.Provider>
        </PanelPickerOverlayContext.Provider>
    );
}
